import { useRef, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  formatBuildingsForFilter,
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '@/reducers/buildingSelectorReducer'
import { selectUserBuilding, selectUserRole } from '@/reducers/userDataReducer'

import { useClickOutside } from '../../helpers/hooks/useClickOutside'
import TextField from '../FormLegacy/FormFieldsV2/TextField'
import PhoneField from '../FormLegacy/FormFieldsV2/PhoneField'
import Filter from '../Filter/Filter'
import API from '../../api/api'

import { getPhoneBodyValue, getPhoneMaxLength, getNewClientValidation } from './helpers'

const NewClientForm = ({ onClose, showUserStatusPopup, refreshClientsTable }) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const [errorMessage, setErrorMessage] = useState(null)
  const ref = useRef(null)
  const timeout = useRef(null)
  useClickOutside(ref, () => onClose())

  const selectedAppBuilding = useSelector(selectActiveBuilding)
  const selectedBuilingId = selectedAppBuilding?.id

  const [buildingId, setBuildingId] = useState(selectedBuilingId)

  const userRole = useSelector(selectUserRole)
  const buildingsForFilter = useSelector(selectBuildingsForFilter)
  const userBuildings = useSelector(selectUserBuilding)
  const buildingsForAddClientModal =
    userRole === 'ShiftManager' ? formatBuildingsForFilter(userBuildings) : buildingsForFilter

  const newClientValidation = useMemo(() => getNewClientValidation(t), [t])

  const validationSchema = Yup.object().shape({
    firstName: newClientValidation.requiredString,
    lastName: newClientValidation.requiredString,
    email: newClientValidation.requiredEmail,
    phone: newClientValidation.phone,
    street: newClientValidation.requiredString,
    apartment: newClientValidation.requiredString,
  })
  const selectedBuilding = useMemo(() => {
    return buildingsForAddClientModal.find((building) => building.id === buildingId)
  }, [buildingsForAddClientModal, buildingId])

  const selectBuilding = ({ id: buildingId }) => {
    setBuildingId(buildingId)
  }

  const handleFormSubmit = async (values, { setSubmitting }) => {
    const filteredValues = Object.keys(values)
      .filter((key) => values[key])
      .reduce((acc, key) => {
        acc[key] = values[key]
        return acc
      }, {})

    const dataToSend = {
      ...filteredValues,
      avatar: 'Nothing',
      code: selectedBuilding.code,
      phone: values.phone ? getPhoneBodyValue(values.phone) : null,
    }

    if (userRole !== 'PropertyManager') {
      API.createNewUserByManager(dataToSend)
        .then(() => {
          showUserStatusPopup({ status: 'created' })
          setSubmitting(false)
          onClose()
          refreshClientsTable()
        })
        .catch((err) => {
          if (err?.response?.status === 409) {
            setSubmitting(false)
            setErrorMessage(t('clients-legacy:newClientForm.emailAlreadyExistsError'))
            timeout.current = setTimeout(() => {
              setErrorMessage(null)
            }, 2000)
          } else {
            setSubmitting(false)
            setErrorMessage(t('clients-legacy:newClientForm.serverError'))
            timeout.current = setTimeout(() => {
              setErrorMessage(null)
            }, 2000)
          }
        })
    } else {
      API.createNewUserByPropertyManager(dataToSend)
        .then(() => {
          showUserStatusPopup({ status: 'created' })
          setSubmitting(false)
          onClose()
          refreshClientsTable()
        })
        .catch((err) => {
          if (err?.response?.status === 409) {
            setSubmitting(false)
            setErrorMessage(t('clients-legacy:newClientForm.emailAlreadyExistsError'))
            timeout.current = setTimeout(() => {
              setErrorMessage(null)
            }, 2000)
          } else {
            setSubmitting(false)
            setErrorMessage(t('clients-legacy:newClientForm.serverError'))
            timeout.current = setTimeout(() => {
              setErrorMessage(null)
            }, 2000)
          }
        })
    }
  }

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  return (
    <div className="new-client-form">
      <Formik
        enableReinitialize={true}
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          street: '',
          apartment: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          setFieldTouched,
          dirty,
          isSubmitting,
          handleSubmit,
        }) => {
          return (
            <div className="new-client-form__container" ref={ref}>
              <div className="window-header">
                <div className="title">{t('clients-legacy:navbar.addResidentButton')}</div>
              </div>
              <div className="main-view">
                <div className="columns">
                  <div className="column is-6">
                    <TextField
                      name="firstName"
                      placeholder={t('clients-legacy:newClientForm.namePlaceholder')}
                      value={values.firstName}
                      isError={touched.firstName && !!errors.firstName}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('firstName')}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="column is-6">
                    <TextField
                      name="lastName"
                      placeholder={t('clients-legacy:newClientForm.surnamePlaceholder')}
                      value={values.lastName}
                      isError={touched.lastName && !!errors.lastName}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('lastName')}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="columns">
                  <div className="column is-6">
                    <PhoneField
                      name="phone"
                      placeholder={t('clients-legacy:table.columns.phoneNumber')}
                      value={values.phone}
                      onChange={handleChange}
                      isError={touched.phone && !!errors.phone}
                      errorLabel={touched.phone && errors.phone}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                      maxLength={getPhoneMaxLength()}
                    />
                  </div>
                  <div className="column is-6">
                    <TextField
                      name="email"
                      placeholder={t('clients-legacy:table.columns.email')}
                      value={values.email}
                      isError={touched.email && !!errors.email}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('email')}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="columns">
                  <div className="column is-9">
                    <TextField
                      name="street"
                      placeholder={t('clients-legacy:newClientForm.addressPlaceholder')}
                      value={values.street}
                      isError={touched.street && !!errors.street}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('street')}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="column is-3">
                    <TextField
                      name="apartment"
                      placeholder={t('clients-legacy:newClientForm.apartmentPlaceholder')}
                      value={values.apartment}
                      isError={touched.apartment && !!errors.apartment}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('apartment')}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                {userRole === 'Administrator' && (
                  <div className="columns">
                    <div className="column is-6">
                      <Filter
                        placeholder={
                          selectedBuilding
                            ? selectedBuilding.text
                            : t('clients:generalInformation.buildingId.placeholder')
                        }
                        options={buildingsForAddClientModal}
                        disabled={
                          !buildingsForAddClientModal ||
                          buildingsForAddClientModal.length <= 1 ||
                          isSubmitting
                        }
                        value={selectedBuilding ? [selectedBuilding] : []}
                        name="Building"
                        additionalClass="filter"
                        variant="rounded"
                        config={{ hasSearch: true }}
                        onChange={(e) => selectBuilding(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="window-footer">
                <div className="columns">
                  <div className="column is-6">
                    {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
                  </div>
                  <div className="column is-3">
                    <button
                      type="submit"
                      className={`button button-gray ${isSubmitting ? 'is-loading' : ''}`}
                      onClick={() => onClose()}
                      disabled={isSubmitting}
                    >
                      {t('common:actions.cancel')}
                    </button>
                  </div>
                  <div className="column is-3">
                    <button
                      type="submit"
                      className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                      onClick={handleSubmit}
                      disabled={!dirty || !selectedBuilding.id || isSubmitting}
                    >
                      {t('common:actions.save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

NewClientForm.propTypes = {
  onClose: PropTypes.func,
  showUserStatusPopup: PropTypes.func,
  refreshClientsTable: PropTypes.func,
}

export default NewClientForm
